/**
 * Educación Parvularia 2026 — Dataset oficial y exclusivo
 *
 * Fuentes autorizadas:
 *   1. "Rúbricas Educación Parvularia_2026 (1).pdf" — texto oficial CPEIP
 *   2. "analisis resultados rubricas educacion parvulo 2026.docx" — resultados 2025,
 *      dificultad, riesgo, recomendaciones específicas para Portafolio Digital Docente
 *
 * REGLA P0:
 *   Este dataset NUNCA se mezcla con `resultados-nacionales.js` (Básica/Media/Generalista).
 *   Todos los IDs usan prefijo `pv-` para evitar colisiones.
 */

/** Discriminador: retorna true si el perfil corresponde a Educación Parvularia. */
export function isParvularia(perfil) {
    if (!perfil) return false;
    const nivel = (perfil.nivel || '').trim().toLowerCase();
    return nivel === 'educación parvularia' || nivel === 'educacion parvularia' || nivel === 'parvularia';
}

/** Escala de riesgo para render visual (color + emoji) */
export const NIVEL_RIESGO_STYLE = {
    'Bajo': { color: 'emerald', emoji: '🟢', label: 'Bajo' },
    'Medio': { color: 'amber', emoji: '🟡', label: 'Medio' },
    'Medio-Alto': { color: 'orange', emoji: '🟠', label: 'Medio-Alto' },
    'Alto': { color: 'orange', emoji: '🟠', label: 'Alto' },
    'Muy Alto': { color: 'rose', emoji: '🔴', label: 'Muy Alto' },
    'Crítico': { color: 'rose', emoji: '🔴', label: 'Crítico' },
};

/** Módulos oficiales de Educación Parvularia 2026 */
export const MODULOS_PARVULARIA = [
    {
        id: 'pv-modulo-1',
        nombre: 'Módulo 1',
        titulo: 'Planificación, evaluación, análisis y reflexión',
        color: 'indigo',
        tareas: [
            {
                id: 'pv-tarea-1',
                nombre: 'Tarea 1',
                titulo: 'Planificación y Evaluación',
                indicadores: [
                    'pv-1-1-1', 'pv-1-2-1', 'pv-1-2-2', 'pv-1-2-3', 'pv-1-3-1',
                ],
            },
            {
                id: 'pv-tarea-2',
                nombre: 'Tarea 2',
                titulo: 'Análisis de la implementación',
                indicadores: ['pv-2-1-1', 'pv-2-2-2'],
            },
            {
                id: 'pv-tarea-3',
                nombre: 'Tarea 3',
                titulo: 'Reflexión sobre el trabajo con las familias',
                indicadores: ['pv-3-3-1'],
            },
        ],
    },
    {
        id: 'pv-modulo-2',
        nombre: 'Módulo 2',
        titulo: 'Grabación de una experiencia para el aprendizaje',
        color: 'emerald',
        tareas: [
            {
                id: 'pv-tarea-4',
                nombre: 'Tarea 4',
                titulo: 'Grabación de una experiencia para el aprendizaje',
                indicadores: [
                    'pv-4-1-1', 'pv-4-1-2', 'pv-4-1-3', 'pv-4-1-4', 'pv-4-1-5',
                    'pv-4-1-6', 'pv-4-1-7', 'pv-4-1-8-sc', 'pv-4-1-8-nmt',
                ],
            },
        ],
    },
    {
        id: 'pv-modulo-3',
        nombre: 'Módulo 3',
        titulo: 'Trabajo Colaborativo para el Desarrollo Profesional',
        color: 'amber',
        tareas: [
            {
                id: 'pv-tarea-5',
                nombre: 'Tarea 5',
                titulo: 'Trabajo Colaborativo para el Desarrollo Profesional',
                indicadores: ['pv-5-1', 'pv-5-2', 'pv-5-3', 'pv-5-4'],
                nota: 'La subtarea 5.4 es INDIVIDUAL.',
            },
        ],
    },
];

/**
 * Rúbricas oficiales por indicador (contenido pedagógico del análisis).
 * Todas las cifras "logrado_2025" / "por_lograr_2025" provienen del documento de análisis.
 */
export const RUBRICAS_PARVULARIA = {
    // ─── MÓDULO 1 ──────────────────────────────────────────────────────────
    'pv-1-1-1': {
        id: 'pv-1-1-1',
        codigo: '1.1.1',
        modulo: 'pv-modulo-1',
        tarea: 'pv-tarea-1',
        nombre: 'Caracterización de los niños y las niñas',
        logrado_2025: 56.7, por_lograr_2025: 43.3,
        dificultad: 'Media', riesgo: 'Bajo', prioridad: 'Baja',
        errores_frecuentes: [
            'Describir al grupo en términos generales sin evidenciar diferencias entre los niños y las niñas.',
            'Limitar la caracterización a antecedentes administrativos o familiares, sin relacionarlos con el aprendizaje.',
            'Mencionar únicamente intereses o únicamente conocimientos previos, cuando el nivel Competente exige considerar ambos.',
            'Abordar solo una o dos áreas del desarrollo cuando el descriptor requiere tres.',
            'Omitir las necesidades de apoyo, los distintos ritmos de aprendizaje o los niveles de desarrollo.',
            'Elaborar descripciones centradas en déficits, sin reconocer fortalezas y potencialidades del grupo.',
        ],
        claves: {
            basico: 'Describir conocimientos previos o intereses; además de considerar al menos dos áreas del desarrollo (cognitiva; sociocultural; socioemocional; motriz).',
            competente: 'Conocimientos previos e intereses; descripción de al menos tres áreas; reconocimiento de la diversidad; identificación de necesidades de apoyo; consideración de ritmos y niveles de desarrollo.',
            destacado: 'Describir los aprendizajes que poseen los niños y niñas en cada uno de los ámbitos de las Bases Curriculares de Educación Parvularia.',
        },
        cambio_2025_2026: 'Sin cambios sustantivos.',
        recomendacion_pdd: 'Construir una caracterización basada en evidencias concretas y no únicamente en apreciaciones generales. Integrar información sobre conocimientos previos, intereses, características cognitivas, socioemocionales, socioculturales y motrices. Explicitar la diversidad existente dentro del grupo, evitando presentar a los niños y niñas como un conjunto homogéneo. Relacionar la caracterización con las decisiones pedagógicas que se adoptarán posteriormente en la planificación. Incorporar referencias a los aprendizajes alcanzados en los distintos ámbitos de las Bases Curriculares cuando se aspire al nivel Destacado.',
    },
    'pv-1-2-1': {
        id: 'pv-1-2-1', codigo: '1.2.1', modulo: 'pv-modulo-1', tarea: 'pv-tarea-1',
        nombre: 'Relación entre experiencias y objetivos',
        logrado_2025: 45.7, por_lograr_2025: 54.3,
        dificultad: 'Intermedio-alto', riesgo: 'Medio', prioridad: 'Alta',
        errores_frecuentes: [
            'Redactar objetivos demasiado generales.',
            'Formular objetivos que no se desprenden claramente de los OA.',
            'Diseñar actividades entretenidas pero poco vinculadas con los objetivos.',
            'Incluir acciones que no aportan al aprendizaje esperado.',
            'Confundir actividades con objetivos.',
            'Construir secuencias didácticas desordenadas.',
            'Aumentar el grado de dificultad sin una progresión lógica.',
        ],
        claves: {
            basico: 'En dos de las tres experiencias, los objetivos específicos se desprenden de los OA y OAT; las experiencias permiten abordar dichos objetivos; todas las acciones propuestas guardan relación con alguno de los objetivos específicos definidos.',
            competente: 'Las mismas condiciones que Básico, pero aplicadas a las tres experiencias presentadas.',
            destacado: 'Existe una secuencia didáctica; dicha secuencia facilita una aproximación progresiva a los objetivos; las actividades se organizan considerando el grado de desafío o abstracción de los aprendizajes.',
        },
        cambio_2025_2026: 'Sin cambios sustantivos.',
        recomendacion_pdd: 'Verificar que cada objetivo específico derive claramente de un OA y, cuando corresponda, de un OAT. Revisar que todas las actividades propuestas contribuyan efectivamente al logro del objetivo. Eliminar acciones que, aunque interesantes, no aporten al aprendizaje esperado. Construir secuencias didácticas con una progresión explícita desde experiencias de menor a mayor complejidad. Justificar la organización de las actividades considerando el nivel de desarrollo del grupo caracterizado.',
    },
    'pv-1-2-2': {
        id: 'pv-1-2-2', codigo: '1.2.2', modulo: 'pv-modulo-1', tarea: 'pv-tarea-1',
        nombre: 'Formulación de focos de observación para el aprendizaje, indicadores o criterios de evaluación',
        logrado_2025: 15.6, por_lograr_2025: 84.4,
        dificultad: 'Muy alta', riesgo: 'Muy Alto', prioridad: 'Muy Alta',
        errores_frecuentes: [
            'Formular focos de observación demasiado generales.',
            'Describir actividades en lugar de desempeños observables.',
            'Utilizar verbos poco evaluables, como "comprender", "conocer" o "valorar", sin explicitar cómo se evidenciarán.',
            'Construir criterios desvinculados de los objetivos específicos.',
            'Omitir aspectos transversales.',
            'No diferenciar los focos según las necesidades de apoyo del grupo.',
        ],
        claves: {
            basico: 'Al menos para una de las experiencias, todos los focos de observación describen conductas o desempeños observables; existe coherencia con los objetivos específicos; al menos uno considera aspectos transversales.',
            competente: 'Las mismas condiciones que Básico, pero aplicadas a todas las experiencias.',
            destacado: 'Formular focos de observación diferenciados según las necesidades de apoyo presentes en el grupo.',
        },
        cambio_2025_2026: 'Sin cambios sustantivos.',
        recomendacion_pdd: 'Redactar focos de observación utilizando conductas claramente observables. Evitar expresiones ambiguas. Verificar que cada foco permita recoger evidencia concreta del objetivo específico. Incorporar al menos un aspecto transversal cuando corresponda. Adaptar algunos criterios a las necesidades de apoyo identificadas en la caracterización del grupo. Revisar sistemáticamente la coherencia entre objetivo, foco de observación y estrategia de evaluación.',
    },
    'pv-1-2-3': {
        id: 'pv-1-2-3', codigo: '1.2.3', modulo: 'pv-modulo-1', tarea: 'pv-tarea-1',
        nombre: 'Relación entre estrategias de evaluación y los focos de observación para el aprendizaje, indicadores o criterios de evaluación',
        logrado_2025: 8.1, por_lograr_2025: 91.9,
        dificultad: 'Crítica', riesgo: 'Crítico', prioridad: 'Crítica',
        errores_frecuentes: [
            'Seleccionar estrategias que no permiten observar los desempeños definidos.',
            'Utilizar la misma estrategia para todos los aprendizajes, sin considerar la naturaleza de los objetivos.',
            'Emplear estrategias poco pertinentes para la edad o el desarrollo de los niños y niñas.',
            'Definir focos de observación que luego no son recogidos mediante la evaluación.',
            'Confundir actividades de enseñanza con estrategias de evaluación.',
            'Presentar estrategias de evaluación demasiado generales o ambiguas.',
        ],
        claves: {
            basico: 'En la mayoría de las experiencias, las estrategias de evaluación permiten recoger información respecto de todos los focos de observación, indicadores o criterios de evaluación definidos.',
            competente: 'Las mismas condiciones que Básico, pero aplicadas a todas las experiencias.',
            destacado: 'Exigir un mayor grado de sofisticación en la selección y utilización de estrategias de evaluación, manteniendo la lógica de diversificación y adecuación a las características del grupo.',
        },
        cambio_2025_2026: 'Sin cambios sustantivos.',
        recomendacion_pdd: 'Revisar cada estrategia de evaluación preguntándose qué foco de observación permite evidenciar. Comprobar que todos los focos definidos cuenten con una estrategia específica para recoger información. Diversificar los procedimientos de evaluación cuando la naturaleza de los aprendizajes así lo requiera. Evitar utilizar instrumentos por costumbre; la estrategia debe responder al objetivo y no al revés. Verificar la coherencia completa entre caracterización, objetivos, focos de observación y estrategias de evaluación.',
    },
    'pv-1-3-1': {
        id: 'pv-1-3-1', codigo: '1.3.1', modulo: 'pv-modulo-1', tarea: 'pv-tarea-1',
        nombre: 'Fundamentación de una experiencia',
        logrado_2025: 29.4, por_lograr_2025: 70.6,
        dificultad: 'Medio-alto', riesgo: 'Medio-Alto', prioridad: 'Alta',
        errores_frecuentes: [
            'Justificar las actividades únicamente porque "motivan" a los niños y niñas.',
            'Fundamentar desde opiniones personales sin evidencia pedagógica.',
            'Repetir información ya presentada en la planificación.',
            'Citar autores sin relacionarlos con la experiencia concreta.',
            'Construir respuestas excesivamente generales.',
        ],
        claves: {
            basico: 'Fundamentar parcialmente las decisiones adoptadas considerando elementos pedagógicos vinculados con la experiencia.',
            competente: 'Fundamentar las decisiones mediante argumentos pedagógicos consistentes, articulando características del grupo; objetivos de aprendizaje; experiencias desarrolladas.',
            destacado: 'Mantener la lógica de profundización observada en el proceso 2025. Valorar una argumentación sólida, integrada y técnicamente consistente.',
        },
        cambio_2025_2026: 'Cambio conceptual moderado.',
        recomendacion_pdd: 'Fundamentar siempre las decisiones considerando la caracterización del grupo. Explicar la relación entre los objetivos de aprendizaje y las estrategias seleccionadas. Utilizar fundamentos pedagógicos pertinentes, evitando citas meramente decorativas. Argumentar desde la evidencia observada en la experiencia y no desde afirmaciones generales. Demostrar cómo las decisiones adoptadas responden a las necesidades reales de los niños y niñas.',
    },
    // ─── MÓDULO 2 ──────────────────────────────────────────────────────────
    'pv-2-1-1': {
        id: 'pv-2-1-1', codigo: '2.1.1', modulo: 'pv-modulo-1', tarea: 'pv-tarea-2',
        nombre: 'Análisis de una experiencia a partir de evidencias y registros del aprendizaje de los niños y las niñas',
        logrado_2025: 11.5, por_lograr_2025: 88.5,
        dificultad: 'Muy alta', riesgo: 'Muy Alto', prioridad: 'Estratégico de alta prioridad',
        errores_frecuentes: [
            'Describir lo ocurrido sin analizar su significado pedagógico.',
            'Limitarse a señalar que los niños y niñas "participaron" o "se motivaron".',
            'Emitir opiniones personales sin respaldarlas en evidencias.',
            'Analizar únicamente a un niño o niña cuando la rúbrica exige interpretar el aprendizaje del grupo.',
            'Repetir información contenida en la planificación.',
            'No relacionar las evidencias con los objetivos de aprendizaje.',
        ],
        claves: {
            basico: 'Describir parcialmente los aprendizajes observados utilizando las evidencias disponibles. El análisis continúa siendo principalmente descriptivo.',
            competente: 'Interpretar las evidencias, relacionarlas con los objetivos de aprendizaje, explicar los resultados observados y fundamentar decisiones pedagógicas.',
            destacado: 'Demostrar una reflexión pedagógica integrada, considerando la diversidad del grupo y proyectando mejoras fundamentadas para futuras intervenciones pedagógicas.',
        },
        cambio_2025_2026: 'Sin cambios sustantivos.',
        recomendacion_pdd: 'Fortalecer las capacidades de interpretación, argumentación y toma de decisiones pedagógicas basadas en evidencia, distinguiendo claramente entre describir e interpretar, y fundamentando todas las conclusiones utilizando evidencias concretas.',
    },
    'pv-2-2-2': {
        id: 'pv-2-2-2', codigo: '2.2.2', modulo: 'pv-modulo-1', tarea: 'pv-tarea-2',
        nombre: 'Análisis de la implementación de una instancia de juego o actividad lúdica',
        logrado_2025: 52.2, por_lograr_2025: 47.8,
        dificultad: 'Intermedio', riesgo: 'Bajo', prioridad: 'Media-baja',
        errores_frecuentes: [
            'Describir el desarrollo del juego sin analizar los aprendizajes.',
            'Centrar el análisis únicamente en el comportamiento de los niños y niñas.',
            'Confundir motivación con aprendizaje.',
            'Limitar la reflexión a aspectos organizativos.',
            'No explicar cómo la intervención de la educadora favoreció el desarrollo de la experiencia.',
            'Proponer mejoras generales sin relacionarlas con las evidencias observadas.',
        ],
        claves: {
            basico: 'Describir parcialmente la implementación de la experiencia lúdica e identificar algunos aspectos relacionados con el aprendizaje observado.',
            competente: 'Analizar la implementación, interpretar los aprendizajes observados, explicar cómo el juego favoreció dichos aprendizajes y fundamentar posibles decisiones pedagógicas.',
            destacado: 'Incorporar un análisis más profundo, que incluya una reflexión integrada sobre la implementación del juego y su contribución al desarrollo de los niños y niñas.',
        },
        cambio_2025_2026: 'Sin cambios sustantivos.',
        recomendacion_pdd: 'Ayudar a las educadoras a profundizar el análisis pedagógico del juego, vinculando las evidencias observadas con los objetivos de aprendizaje, la intervención docente y las decisiones de mejora, para que el juego sea analizado como una experiencia de aprendizaje intencionada.',
    },
    // ─── MÓDULO 1 · TAREA 3 · Reflexión sobre trabajo con familias ─────────
    'pv-3-3-1': {
        id: 'pv-3-3-1', codigo: '3.3.1', modulo: 'pv-modulo-1', tarea: 'pv-tarea-3',
        nombre: 'Reflexión sobre el trabajo con las familias de los niños y las niñas',
        logrado_2025: 36.9, por_lograr_2025: 63.1,
        dificultad: 'Media-alta', riesgo: 'Medio-Alto', prioridad: 'Alta',
        errores_frecuentes: [
            'Enumerar actividades realizadas con las familias sin analizarlas.',
            'Confundir participación o asistencia con impacto pedagógico.',
            'Afirmar que la experiencia fue positiva sin presentar evidencias.',
            'Concentrarse exclusivamente en las dificultades de las familias.',
            'Responsabilizar a madres, padres o cuidadores por una baja participación.',
            'No analizar las propias decisiones profesionales.',
            'No vincular el trabajo con las familias con el aprendizaje, bienestar o desarrollo de los niños y niñas.',
            'Formular mejoras generales que no se derivan de la experiencia analizada.',
        ],
        claves: {
            basico: 'Describir una experiencia de trabajo con las familias e identificar algunos aspectos relevantes de dicha experiencia.',
            competente: 'Analizar el trabajo con las familias, explicar su aporte al aprendizaje y bienestar, identificar fortalezas y aspectos susceptibles de mejora, y proyectar decisiones profesionales derivadas del análisis.',
            destacado: 'Demostrar una reflexión integrada que considere el impacto del trabajo con las familias y proyecte mejoras sustentadas en el análisis realizado.',
        },
        cambio_2025_2026: 'Sin cambios sustantivos.',
        recomendacion_pdd: 'Ayudar a las educadoras a estructurar su reflexión, verificando que identifique un propósito pedagógico, utilice evidencias, analice resultados, reconozca la diversidad de las familias, examine críticamente sus propias decisiones, vincule la experiencia con el aprendizaje infantil y proyecte mejoras concretas. Distinguir claramente entre niveles de elaboración: Descripción, Análisis y Reflexión profesional.',
    },
    // ─── MÓDULO 2 · Grabación ──────────────────────────────────────────────
    'pv-4-1-1': {
        id: 'pv-4-1-1', codigo: '4.1.1', modulo: 'pv-modulo-2', tarea: 'pv-tarea-4',
        nombre: 'Ambiente bientratante',
        logrado_2025: 82.4, por_lograr_2025: 17.6,
        dificultad: 'Baja', riesgo: 'Bajo', prioridad: 'Media-baja',
        errores_frecuentes: [
            'Buen trato solo aparente o superficial.', 'Respuestas tardías.',
            'Atención concentrada en algunos niños y niñas.', 'Falta de anticipación.',
            'Interrupciones o control excesivo.', 'Espacio rígido o poco seguro.',
            'Respuesta homogénea frente a necesidades distintas.',
        ],
        claves: {
            basico: 'Las relaciones respetuosas y respuestas sensibles se observan durante la mayor parte del tiempo.',
            competente: 'Ambas condiciones se sostienen durante todo el tiempo.',
            destacado: 'Además, el espacio, mobiliario y recursos se disponen intencionada y flexiblemente para garantizar bienestar, seguridad y exploración confiada.',
        },
        cambio_2025_2026: 'Sin cambios.',
        recomendacion_pdd: 'Revisar si el espacio permite desplazamiento seguro; si los materiales están accesibles; si la disposición facilita la autonomía; si existen alternativas para distintos intereses o necesidades; si las transiciones están previstas; si el mobiliario puede ajustarse durante la experiencia. Mantener un tono cálido y calmado; establecer contacto visual; anticipar acciones y cambios; observar expresiones corporales; atender tanto a quienes participan como a quienes se retraen; responder oportunamente; proporcionar apoyos sin anular la autonomía; reconocer esfuerzos y participación; comunicar expectativas positivas.',
    },
    'pv-4-1-2': {
        id: 'pv-4-1-2', codigo: '4.1.2', modulo: 'pv-modulo-2', tarea: 'pv-tarea-4',
        nombre: 'Contribución de una experiencia para el aprendizaje a los objetivos',
        logrado_2025: 47.1, por_lograr_2025: 52.9,
        dificultad: 'Media-alta', riesgo: 'Medio-Alto', prioridad: 'Alta',
        errores_frecuentes: [
            'Formular objetivos que no se desprenden claramente de los OA u OAT.',
            'Declarar tres o más objetivos sin tiempo suficiente para desarrollarlos.',
            'Realizar preguntas que no se relacionan con los objetivos.',
            'Concentrar la experiencia en un solo objetivo y dejar los demás en segundo plano.',
            'Incluir materiales llamativos sin una función pedagógica clara.',
            'Confundir participación con logro de los objetivos.',
            'Declarar un OAT que no se evidencia en las acciones implementadas.',
            'Presentar una secuencia de actividades, pero no una verdadera progresión didáctica.',
            'Desarrollar el objetivo principalmente mediante la intervención de la educadora, sin generar oportunidades suficientes para que los niños y niñas actúen en torno a él.',
        ],
        claves: {
            basico: 'Existe coherencia entre al menos la mitad de los objetivos específicos y las acciones implementadas por la educadora.',
            competente: 'Existe coherencia entre todos los objetivos específicos y las acciones implementadas.',
            destacado: 'La experiencia presenta una secuencia didáctica que facilita una aproximación progresiva a los objetivos específicos, considerando el grado de desafío o el grado de abstracción con que se abordan.',
        },
        cambio_2025_2026: 'Sin cambios sustantivos.',
        recomendacion_pdd: 'Reducir y priorizar objetivos. Revisar cada objetivo por separado para identificar acciones y evidencias. Construir una matriz de alineamiento. Verificar la grabación desde la perspectiva del evaluador. Hacer visible el OAT. Asegurar una progresión didáctica.',
    },
    'pv-4-1-3': {
        id: 'pv-4-1-3', codigo: '4.1.3', modulo: 'pv-modulo-2', tarea: 'pv-tarea-4',
        nombre: 'Promoción del desarrollo del lenguaje',
        logrado_2025: 87.4, por_lograr_2025: 12.6,
        dificultad: 'Baja', riesgo: 'Bajo', prioridad: 'Media-baja',
        errores_frecuentes: [
            'Hablar en exceso.', 'Formular solo preguntas cerradas.',
            'No responder a los intentos comunicativos.', 'Repetir sin ampliar.',
            'Utilizar estrategias del mismo tipo.', 'Introducir vocabulario sin contexto.',
            'Confundir instrucciones con desarrollo del lenguaje.',
            'Alfabetización temprana reducida a mostrar letras.',
        ],
        claves: {
            basico: 'Implementa algunas estrategias para facilitar el desarrollo del lenguaje en su dimensión oral, escrita o ambas, considerando aspectos de la comunicación verbal, no verbal o paraverbal.',
            competente: 'Implementa estrategias múltiples (al menos tres) y diversas (al menos de dos tipos) que faciliten el desarrollo del lenguaje oral, escrito o ambos, considerando formas de comunicación verbal, no verbal o paraverbal.',
            destacado: 'Para Sala Cuna: modela la conversación mediante intercambios verbales, indicaciones preverbales, atención a tentativas de comunicación y reacción pertinente. Para Nivel Medio y Transición: promueve el uso de palabras con distintas funciones, vocabulario específico o una estrategia de alfabetización temprana que acerque e interese al código escrito.',
        },
        cambio_2025_2026: 'Sin cambios.',
        recomendacion_pdd: 'Planificar anticipadamente al menos tres estrategias de dos tipos diferentes. Asegurar el tiempo para responder, realizar preguntas de seguimiento, retomar ideas, ampliar expresiones, considerar gestos y no monopolizar la conversación. Para Destacado, enfocarse en el modelamiento conversacional, el enriquecimiento funcional del vocabulario o la alfabetización temprana.',
    },
    'pv-4-1-4': {
        id: 'pv-4-1-4', codigo: '4.1.4', modulo: 'pv-modulo-2', tarea: 'pv-tarea-4',
        nombre: 'Promoción del protagonismo de los niños y las niñas',
        logrado_2025: 37.3, por_lograr_2025: 62.7,
        dificultad: 'Alta', riesgo: 'Alto', prioridad: 'Muy Alta',
        errores_frecuentes: [
            'Confundir participación con protagonismo.', 'Experiencias excesivamente dirigidas.',
            'Temor a perder el control durante la grabación.', 'Opciones poco significativas.',
            'Intervención anticipada.', 'Participación concentrada en algunos niños.',
            'Desafío poco ajustado.', 'Ludicidad interpretada como entretención.',
            'Presentar alternativas únicamente al comienzo, sin permitir nuevas decisiones.',
        ],
        claves: {
            basico: 'Existe al menos una oportunidad donde los niños y niñas participan activamente mediante acciones como tomar decisiones, resolver problemas o explorar.',
            competente: 'Las interacciones desafiantes y lúdicas predominan durante la mayor parte de la experiencia y se ofrece una alternativa ante momentos de desinterés.',
            destacado: 'Además, se ofrecen al menos tres alternativas distintas para que los niños y niñas puedan decidir el rumbo de su experiencia.',
        },
        cambio_2025_2026: 'Eliminación de rúbrica en Módulo 1; permanencia en Módulo 2 con cambio menor/editorial.',
        recomendacion_pdd: 'Asegurar que exista al menos una oportunidad clara de participación activa y lúdica. Promover que el protagonismo predomine durante la experiencia y responder al desinterés. Ofrecer al menos tres alternativas concretas que permitan a los niños decidir el rumbo de la experiencia, asegurando que las opciones sean pedagógicamente relevantes.',
    },
    'pv-4-1-5': {
        id: 'pv-4-1-5', codigo: '4.1.5', modulo: 'pv-modulo-2', tarea: 'pv-tarea-4',
        nombre: 'Mediación de los aprendizajes',
        logrado_2025: 59.2, por_lograr_2025: 40.8,
        dificultad: 'Intermedia', riesgo: 'Medio', prioridad: 'Alta',
        errores_frecuentes: [
            'Utilizar dos preguntas como si fueran dos estrategias distintas.', 'Explicar demasiado.',
            'Resolver el problema por los niños.', 'Formular preguntas desconectadas del objetivo.',
            'Aplicar la misma estrategia a todo el grupo.', 'Confundir variedad con acumulación.',
            'No ofrecer alternativas de expresión.',
            'Presentar conexiones cotidianas en lugar de promoverlas.',
            'Trabajo grupal sin aprendizaje social.', 'Confirmar un error conceptual.',
        ],
        claves: {
            basico: 'Utiliza al menos dos estrategias de mediación pertinentes para el grupo y orientadas a los objetivos.',
            competente: 'Además, al menos una estrategia es diversificada para acceder al aprendizaje o expresar lo aprendido.',
            destacado: 'Además, promueve la conexión con experiencias cotidianas o el aprendizaje social.',
        },
        cambio_2025_2026: 'Sin cambios sustantivos, pero con cambio formal en la ubicación de la regla sobre error conceptual.',
        recomendacion_pdd: 'Planificar al menos dos estrategias diferenciadas y pertinentes para el grupo. Asegurar que al menos una estrategia ofrezca distintas opciones de acceso o expresión. Incorporar de manera visible al menos una conexión con experiencias cotidianas o aprendizaje social. Prevenir errores conceptuales, evitando confirmar explícitamente contenidos erróneos.',
    },
    'pv-4-1-6': {
        id: 'pv-4-1-6', codigo: '4.1.6', modulo: 'pv-modulo-2', tarea: 'pv-tarea-4',
        nombre: 'Retroalimentación de los aprendizajes de los niños y niñas',
        logrado_2025: 25.4, por_lograr_2025: 74.6,
        dificultad: 'Alta', riesgo: 'Muy Alto', prioridad: 'Muy Alta',
        errores_frecuentes: [
            'Entregar inmediatamente la respuesta correcta.', 'Repetir exactamente la misma instrucción.',
            'Utilizar preguntas retóricas.', 'Hacer preguntas sin esperar respuesta.',
            'Corregir sin explicar ni orientar.', 'Ayudar demasiado.', 'Retroalimentar solo errores.',
            'Utilizar comentarios generales.', 'No basarse en la observación.',
            'Realizar solo una o dos intervenciones específicas.',
        ],
        claves: {
            basico: 'Entrega información adicional, aclaraciones o la respuesta correcta.',
            competente: 'Proporciona ayuda, preguntas o consejos para facilitar que los niños y niñas alcancen o profundicen su desempeño (tres ocasiones).',
            destacado: 'Implementa sistemáticamente acciones de retroalimentación (cuatro o más ocasiones).',
        },
        cambio_2025_2026: 'Cambio moderado de operacionalización (definición de frecuencia para Competente y Destacado).',
        recomendacion_pdd: 'Asegurar al menos tres episodios de retroalimentación ajustada y oportuna. Utilizar preguntas, pistas o consejos que permitan al niño avanzar. Diferenciar claramente entre entregar la respuesta y facilitar el avance. Para Destacado, asegurar cuatro o más ocasiones de retroalimentación. Evitar la confusión entre aclaración y retroalimentación para avanzar.',
    },
    'pv-4-1-7': {
        id: 'pv-4-1-7', codigo: '4.1.7', modulo: 'pv-modulo-2', tarea: 'pv-tarea-4',
        nombre: 'Reconocimiento a los logros y esfuerzos de los niños y las niñas',
        logrado_2025: 33.3, por_lograr_2025: 66.7,
        dificultad: 'Alta', riesgo: 'Alto', prioridad: 'Muy Alta',
        errores_frecuentes: [
            'Utilizar solamente "muy bien".', 'Reconocer sin haber observado.',
            'Felicitar solo el resultado final.', 'Reconocer solo a quienes responden verbalmente.',
            'Utilizar solo reconocimiento verbal.', 'Utilizar solo gestos.',
            'Formular elogios sobre características personales.', 'Comparar entre niños.',
            'Reconocer todo de manera indiscriminada.', 'Realizar solo una o dos intervenciones específicas.',
        ],
        claves: {
            basico: 'Ofrece reconocimientos verbales o gestuales, dirigidos de forma general al grupo, sin indicar claramente qué aspecto está reforzando.',
            competente: 'Ofrece reconocimientos verbales y gestuales, dirigidos individual o grupalmente, que identifican claramente el aspecto valorado (tres ocasiones).',
            destacado: 'Realiza esta conducta sistemáticamente (cuatro o más ocasiones).',
        },
        cambio_2025_2026: 'Cambio moderado de operacionalización (definición de frecuencia para Competente y Destacado).',
        recomendacion_pdd: 'Asegurar al menos tres reconocimientos específicos, verbales y gestuales, que identifiquen claramente el aspecto valorado. Para Destacado, observar cuatro o más ocasiones, reconociendo distintos tipos de desempeño (logro, estrategia, esfuerzo, colaboración, etc.). Diferenciar entre elogio general y reconocimiento específico.',
    },
    'pv-4-1-8-sc': {
        id: 'pv-4-1-8-sc', codigo: '4.1.8 (Sala Cuna)', modulo: 'pv-modulo-2', tarea: 'pv-tarea-4',
        nombre: 'Promoción de la equidad de género en el aula — Sala Cuna',
        logrado_2025: 35.6, por_lograr_2025: 64.4,
        dificultad: 'Alta', riesgo: 'Alto', prioridad: 'Muy Alta',
        variante: 'Sala Cuna',
        errores_frecuentes: [
            'Considerar que formar grupos mixtos es suficiente.',
            'Declarar que "todos pueden participar" sin mostrar decisiones concretas.',
            'Utilizar recursos que reproducen roles tradicionales.',
            'Reforzar comentarios estereotipados.', 'Asignar roles por género.',
            'Confundir neutralidad con desnaturalización.',
            'Preparar una ficha sólida, pero utilizar recursos contradictorios.',
            'Forzar una conversación poco adecuada para el nivel.',
            'Incorporar un referente aislado.', 'Invertir el estereotipo sin ampliar las posibilidades.',
        ],
        claves: {
            basico: 'Intenciona un diseño pedagógico que no reproduzca sesgos o estereotipos de género, o promueve igualdad de oportunidades para la participación de niñas y niños.',
            competente: 'Intenciona un diseño pedagógico que desnaturalice sesgos o estereotipos de género, o promueve que niñas y niños asuman roles que desnaturalicen sesgos o estereotipos durante las interacciones.',
            destacado: 'Desnaturaliza sesgos o estereotipos en distintas oportunidades mediante el diseño pedagógico, o entrega oportunidades para que niñas y niños reflexionen sobre sesgos o estereotipos.',
        },
        cambio_2025_2026: 'Sin cambios sustantivos.',
        recomendacion_pdd: 'Revisar los recursos, la organización y las interacciones del diseño pedagógico para asegurar que no reproduzcan sesgos o estereotipos. Evitar la discriminación directa y centrarse en desnaturalizar activamente los estereotipos. Para Sala Cuna, el foco está en el diseño.',
    },
    'pv-4-1-8-nmt': {
        id: 'pv-4-1-8-nmt', codigo: '4.1.8 (Nivel Medio y Transición)', modulo: 'pv-modulo-2', tarea: 'pv-tarea-4',
        nombre: 'Promoción de la equidad de género en el aula — Nivel Medio y Transición',
        logrado_2025: 35.6, por_lograr_2025: 64.4,
        dificultad: 'Alta', riesgo: 'Alto', prioridad: 'Muy Alta',
        variante: 'Nivel Medio y Transición',
        errores_frecuentes: [
            'Considerar que formar grupos mixtos es suficiente.',
            'Declarar que "todos pueden participar" sin mostrar decisiones concretas.',
            'Utilizar recursos que reproducen roles tradicionales.',
            'Reforzar comentarios estereotipados.', 'Asignar roles por género.',
            'Confundir neutralidad con desnaturalización.',
            'Preparar una ficha sólida, pero utilizar recursos contradictorios.',
            'Forzar una conversación poco adecuada para el nivel.',
            'Incorporar un referente aislado.', 'Invertir el estereotipo sin ampliar las posibilidades.',
        ],
        claves: {
            basico: 'Intenciona un diseño pedagógico que no reproduzca sesgos o estereotipos de género, o promueve igualdad de oportunidades para la participación de niñas y niños.',
            competente: 'Intenciona un diseño pedagógico que desnaturalice sesgos o estereotipos de género, o promueve que niñas y niños asuman roles que desnaturalicen sesgos o estereotipos durante las interacciones.',
            destacado: 'Desnaturaliza sesgos o estereotipos en distintas oportunidades mediante el diseño pedagógico, o entrega oportunidades para que niñas y niños reflexionen sobre sesgos o estereotipos.',
        },
        cambio_2025_2026: 'Sin cambios sustantivos.',
        recomendacion_pdd: 'Asegurar la igualdad de participación, evitar la asignación de roles por género, cuestionar comentarios estereotipados y ofrecer referentes diversos. Para Competente, desnaturalizar activamente los estereotipos mediante el diseño o los roles. Para Destacado, hacerlo en distintas oportunidades o promover la reflexión infantil.',
    },
    // ─── MÓDULO 3 ──────────────────────────────────────────────────────────
    'pv-5-1': {
        id: 'pv-5-1', codigo: '5.1', modulo: 'pv-modulo-3', tarea: 'pv-tarea-5',
        nombre: 'Relevancia del problema a partir del análisis de evidencia',
        logrado_2025: 88.7, por_lograr_2025: 11.3,
        dificultad: 'Baja (2025) / Media (2026)', riesgo: 'Medio', prioridad: 'Alta',
        errores_frecuentes: [
            'Adjuntar evidencia pero no analizarla.', 'Describir datos sin interpretarlos.',
            'Identificar un problema correcto pero sin demostrar su relevancia.',
            'Formular conclusiones antes de presentar los hallazgos.',
            'Usar evidencia que no se relaciona directamente con el problema.',
            'Introducir teoría sin conectarla con la evidencia.',
            'Presentar varias fuentes sin integrarlas.',
            'Explicar desde la experiencia personal sin fundamentación empírica.',
            'Confundir "tener evidencia" con "analizar evidencia".',
        ],
        claves: {
            basico: 'Presenta un problema o necesidad relacionado con el proceso de enseñanza y aprendizaje, abordado en la experiencia colaborativa y coherente con la evidencia, pero la explicación de su relevancia no se basa en el análisis de la evidencia.',
            competente: 'Funda la relevancia del problema para el aprendizaje de sus estudiantes basándose en el análisis de la evidencia revisada.',
            destacado: 'Enriquece el análisis integrando información de distinta naturaleza (datos cuantitativos, cualitativos, contextuales, referenciales) para comprender la relevancia del problema.',
        },
        cambio_2025_2026: 'Modificación sustantiva: incorporación explícita y central del análisis de evidencia.',
        recomendacion_pdd: 'Demostrar cómo la evidencia analizada fundamenta la relevancia del problema. No basta con adjuntar evidencia, es necesario analizarla. Para Destacado, integrar información de distintas fuentes para una comprensión más profunda.',
    },
    'pv-5-2': {
        id: 'pv-5-2', codigo: '5.2', modulo: 'pv-modulo-3', tarea: 'pv-tarea-5',
        nombre: 'Diálogo pedagógico en torno a la implementación del trabajo colaborativo',
        logrado_2025: 21.0, por_lograr_2025: 79.0,
        dificultad: 'Muy alta', riesgo: 'Muy Alto', prioridad: 'Muy Alta',
        errores_frecuentes: [
            'No cumplir con la condición del nivel Básico.',
            'Confundir acuerdo o coordinación con reflexión pedagógica.',
            'Describir procesos sin mostrar el razonamiento colectivo.',
            'Presentar una sola perspectiva.', 'Omitir diferencias de opinión o contraargumentos.',
            'No relacionar aprendizaje con enseñanza de manera explícita.',
            'Atribuir la dificultad exclusivamente a los estudiantes.',
            'Presentar la solución antes del análisis.',
            'Insertar teoría de forma superficial.',
            'Describir acciones administrativas en lugar de pedagógicas.',
        ],
        claves: {
            basico: 'Existe una propuesta conjunta centrada en aspectos pedagógicos, pero sin dar cuenta de un proceso reflexivo.',
            competente: 'Demuestra reflexión pedagógica conjunta, establece una relación entre aprendizaje y práctica de enseñanza, y toma una decisión fundada.',
            destacado: 'Además, profundiza la reflexión integrando fundamentos teóricos, rol/misión docente o motivaciones profesionales.',
        },
        cambio_2025_2026: 'Continuidad sustantiva con ajustes menores.',
        recomendacion_pdd: 'Demostrar el proceso reflexivo conjunto (preguntas, hipótesis, interpretaciones, contrastes, análisis, decisiones). Relacionar explícitamente el aprendizaje de los estudiantes con la práctica pedagógica. Para Destacado, profundizar la reflexión a través de fundamentos teóricos, rol docente o motivaciones profesionales.',
    },
    'pv-5-3': {
        id: 'pv-5-3', codigo: '5.3', modulo: 'pv-modulo-3', tarea: 'pv-tarea-5',
        nombre: 'Evaluación conjunta de la forma de trabajar en la experiencia colaborativa',
        logrado_2025: 55.1, por_lograr_2025: 44.9,
        dificultad: 'Media', riesgo: 'Medio', prioridad: 'Alta',
        errores_frecuentes: [
            'Describir formas de trabajo sin evaluarlas.',
            'Mencionar solo aspectos positivos o solo dificultades.',
            'Identificar facilitadores y obstáculos sin explicar por qué influyeron.',
            'Centrar el análisis en relaciones personales sin vincularlas con los objetivos.',
            'Decir "aprendimos mucho" sin precisar qué.',
            'Formular aprendizajes muy generales.',
            'Proponer una aplicación futura sin relación con lo analizado.',
            'Confundir resultados pedagógicos con evaluación del proceso colaborativo.',
        ],
        claves: {
            basico: 'Explica solo un aspecto (facilitador o obstáculo) de las formas de trabajo implementadas que influyó en la consecución de los objetivos.',
            competente: 'Explica por qué algunos aspectos facilitaron y otros obstaculizaron la consecución de los objetivos, y señala un aprendizaje transferible a otras experiencias.',
            destacado: 'Además, describe concretamente cómo implementaría ese aprendizaje en otra experiencia colaborativa.',
        },
        cambio_2025_2026: 'Continuidad de la rúbrica con cambio estructural relevante (de voluntaria a integrada).',
        recomendacion_pdd: 'Evaluar críticamente las formas de trabajo, identificando facilitadores y obstáculos y explicando su influencia. Extraer un aprendizaje concreto y aplicable a futuras experiencias. Para Destacado, describir detalladamente la implementación de ese aprendizaje.',
    },
    'pv-5-4': {
        id: 'pv-5-4', codigo: '5.4', modulo: 'pv-modulo-3', tarea: 'pv-tarea-5',
        nombre: 'Aprendizajes profesionales a partir de la experiencia de trabajo colaborativo',
        logrado_2025: 62.0, por_lograr_2025: 38.0,
        dificultad: 'Media-baja', riesgo: 'Medio', prioridad: 'Alta',
        es_individual: true,
        errores_frecuentes: [
            'Señalar "aprendí nuevas estrategias" sin especificarlas.',
            'Afirmar "mejoré mi práctica" sin describir cómo.',
            'Decir "los niños mejoraron" sin explicar o ejemplificar.',
            'Relatar el trabajo del equipo en lugar del aprendizaje individual.',
            'Describir la experiencia colaborativa completa sin centrarse en el aprendizaje profesional.',
            'Mencionar una situación concreta sin relacionarla con el aprendizaje adquirido.',
            'Afirmar una repercusión sin evidencia observable.',
            'Presentar una reflexión idealizada.',
            'Omitir dificultades o aspectos pendientes para Destacado.',
            'Confundir aprendizaje del equipo con aprendizaje profesional individual.',
        ],
        claves: {
            basico: 'Reconoce el aporte de la experiencia colaborativa e identifica lo aprendido, pero lo explica de manera general y sin ilustrarlo con situaciones concretas o explicar la repercusión en los estudiantes.',
            competente: 'Ilustra con situaciones concretas cómo el aprendizaje se manifiesta en su práctica y explica o ejemplifica la repercusión en los estudiantes.',
            destacado: 'Además, da cuenta de dificultades, cuestionamientos, aprensiones o aspectos menos logrados de su propio aprendizaje profesional.',
        },
        cambio_2025_2026: 'Continuidad sustantiva con ajustes menores de precisión.',
        recomendacion_pdd: 'Demostrar el aprendizaje profesional individual con situaciones concretas de práctica y explicar o ejemplificar la repercusión en los estudiantes. Para Destacado, reflexionar críticamente sobre el propio proceso, reconociendo dificultades o aspectos pendientes. Es crucial presentar el aprendizaje individual, no solo el del equipo.',
    },
};

/** Lista de indicadores en orden módulo → tarea → índice */
export const INDICADORES_PARVULARIA = Object.values(RUBRICAS_PARVULARIA);

/** Hallazgos transversales del análisis 2025-2026 */
export const HALLAZGOS_TRANSVERSALES_PV = [
    'La mayoría de las rúbricas del Módulo 1 (Planificación y Evaluación) presentan estabilidad conceptual y de criterios entre 2025 y 2026.',
    'Varios indicadores del Módulo 2 muestran resultados nacionales de logro muy bajos (entre 8,1% y 37,3%), lo que indica dificultades significativas en la demostración práctica.',
    'La retroalimentación explicita ahora frecuencia mínima: tres intervenciones para Competente y cuatro o más para Destacado.',
    'El indicador "Promoción del protagonismo de los niños y las niñas" presenta una brecha de casi 50 puntos porcentuales entre planificación (86,0% en 2025) e implementación observada (37,3% en 2025).',
    'La rúbrica 5.1 introduce un cambio sustantivo: exige fundamentar la relevancia del problema mediante análisis de evidencia.',
    'La rúbrica 5.2 tuvo apenas 21,0% de logro nacional 2025, señalando alta dificultad para la reflexión conjunta.',
    'La ausencia de respaldo documental para el trabajo colaborativo resulta en Insatisfactorio para todos los indicadores de la Tarea 5.',
];

/** Estadísticas rápidas para el Dashboard */
export function computeStatsParvularia() {
    const totalRubricas = INDICADORES_PARVULARIA.length;
    const porModulo = MODULOS_PARVULARIA.reduce((acc, m) => {
        acc[m.id] = INDICADORES_PARVULARIA.filter((r) => r.modulo === m.id).length;
        return acc;
    }, {});
    const criticos = INDICADORES_PARVULARIA.filter((r) => ['Muy Alto', 'Crítico'].includes(r.riesgo)).length;
    const bajoRiesgo = INDICADORES_PARVULARIA.filter((r) => r.riesgo === 'Bajo').length;
    return { totalRubricas, porModulo, criticos, bajoRiesgo };
}

export const RUBRICA_POR_ID = RUBRICAS_PARVULARIA;

/* ═════════════════════════════════════════════════════════════════════
   Helpers para el modal "Nuevo Borrador" y flujo Parvularia end-to-end
   ═════════════════════════════════════════════════════════════════════ */

/** Devuelve los módulos de Parvularia con id/nombre/título (para selector UI). */
export function getModulosParvulariaSelectItems() {
    return MODULOS_PARVULARIA.map((m) => ({
        id: m.id,
        nombre: m.nombre,          // "Módulo 1"
        titulo: m.titulo,          // "Planificación, evaluación..."
        label: `${m.nombre} — ${m.titulo}`,
    }));
}

/** Tareas de un módulo (para el selector "Tarea" cuando nivel=PV). */
export function getTareasParvulariaPorModulo(moduloId) {
    const mod = MODULOS_PARVULARIA.find((m) => m.id === moduloId);
    if (!mod) return [];
    return mod.tareas.map((t) => ({
        id: t.id,
        nombre: t.nombre,          // "Tarea 1"
        titulo: t.titulo,
        label: `${t.nombre} — ${t.titulo}`,
    }));
}

/** Indicadores de una tarea de Parvularia (para el selector "Indicador"). */
export function getIndicadoresParvulariaPorTarea(tareaId) {
    for (const mod of MODULOS_PARVULARIA) {
        const tarea = mod.tareas.find((t) => t.id === tareaId);
        if (!tarea) continue;
        return (tarea.indicadores || []).map((indId) => {
            const rub = RUBRICAS_PARVULARIA[indId];
            return {
                id: indId,
                nombre: rub ? `${rub.codigo} · ${rub.nombre}` : indId,
                codigo: rub?.codigo || indId,
            };
        });
    }
    return [];
}

/** Discriminador robusto por nivel: acepta "PV", "Educación Parvularia", "parvularia". */
export function nivelEsParvularia(nivel) {
    if (!nivel) return false;
    const n = String(nivel).trim().toLowerCase();
    return n === 'pv' || n === 'parvularia' || n === 'educación parvularia' || n === 'educacion parvularia';
}

/** Diagnóstico compatible con el formato usado por Borradores (color, clasificación). */
export function getDiagnosticoParvularia(indicadorId) {
    const rub = RUBRICAS_PARVULARIA[indicadorId];
    if (!rub) return null;
    const porLograr = Number(rub.por_lograr_2025 ?? 0);
    const logrado = Number(rub.logrado_2025 ?? 0);
    let clasificacion = 'BAJA', color = 'green';
    let orientacion = 'Buen desempeño nacional 2025. Para Destacado, profundiza en fundamentación y evidencia observable.';
    if (porLograr >= 50) {
        clasificacion = 'ALTA'; color = 'red';
        orientacion = `Indicador crítico en Parvularia (${porLograr.toFixed(1)}% aún por lograr en 2025). Prioriza este indicador.`;
    } else if (porLograr >= 20) {
        clasificacion = 'MEDIA'; color = 'amber';
        orientacion = `Dificultad intermedia (${porLograr.toFixed(1)}% por lograr en 2025). Cuida la coherencia entre planificación, implementación y evaluación.`;
    }
    return {
        indicadorId,
        codigo: rub.codigo,
        nombre: rub.nombre,
        clasificacion,
        color,
        orientacion,
        // Cifras nacionales (mismo shape que resultados-nacionales para render)
        insatisfactorio: null,
        basico: null,
        competente: null,
        destacado: null,
        // Extra parvularia
        logrado_2025: logrado,
        por_lograr_2025: porLograr,
        dificultad: rub.dificultad,
        riesgo: rub.riesgo,
        prioridad: rub.prioridad,
        errores_frecuentes: rub.errores_frecuentes,
        claves: rub.claves,
        recomendacion_pdd: rub.recomendacion_pdd,
        es_parvularia: true,
    };
}
